import { FiltersService } from 'src/app/core/services/filters.service';
import { Component, OnInit, DoCheck, OnDestroy, Inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { AlertService } from './core/services/alert.service';
import { Alert, ONE_CUSTOMER_TO_EBR_REGION_MAP } from './global/types';
import { AuthenticationService } from "./core/services/authentication.service";
import { MixpanelService } from "./core/services/mixpanel.service";
import { ContractModel } from 'src/app/global/contract-filter-model';
import { TitleService } from './core/services/title.service';
import { Filters } from './global/filter-model';
import { BrandingService } from './core/services/branding.service';
import { NavigationEnd, Router } from '@angular/router';
import { IdleService } from './core/services/idle.service';
import { TelemetryService } from './core/services/telemetry/telemetryservice';
import { FEATURE_FLAGS, FeatureFlags, STARTUP_PARAMS, StartupParams } from './config/AppInitializer.factory';
import { GLOBAL_CACHE_DATA, GlobalCacheData } from './config/GlobalCacheLoader.service';
import { PolicyService } from './core/services/policy.service';
import { CountriesService } from './core/services/countries.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy, DoCheck {
  title = 'ebr-dashboard';
  filters: ContractModel | undefined;
  filtersSubscription: Subscription | undefined;
  alerts: Alert[] | undefined;
  alertsSubscription: Subscription | undefined;
  isAuthenticated: boolean= false;
  isUserAuthorized: boolean = true;
  warningMessage: string = '';
  authenticationSubscription: Subscription | undefined;


  confirmedFilters: Filters | undefined;
  confirmedFiltersSubscription: Subscription | undefined;
  serviceCallData: any = [];
  readingObservationData: any = [];
  pageTitle: string = "Home";

  showTechnicalIssuesBanner: boolean = false;

  constructor(
    private filtersService: FiltersService,
    private swUpdate: SwUpdate,
    private alertService: AlertService,
    private authenticationService: AuthenticationService,
    private mixPanelService: MixpanelService,
    private titleService: TitleService,
    private _brandingService: BrandingService,
    private _router: Router,
    private _telemetryService: TelemetryService,
    private _idleService: IdleService,
    @Inject(FEATURE_FLAGS) private _featureFlags: FeatureFlags,
    @Inject(STARTUP_PARAMS) private _startupQueryParams: StartupParams,
    @Inject(GLOBAL_CACHE_DATA) private _globalCacheData: GlobalCacheData,
    private _policyService: PolicyService,
    private _countriesService: CountriesService,
  ) { }

  get technicalIssuesMessage() {
    return this._brandingService.technicalIssuesMessage
  }

  ngDoCheck(): void {
    this.filtersSubscription = this.filtersService.selectedFiltersObservable.subscribe(f => this.filters = f);
    this.alertsSubscription = this.alertService.alertsObservable.subscribe(a => this.alerts = a);
  }

  ngOnInit(): void {
    document.body.classList.add('neptune-ui-enabled');

    this._telemetryService.logEvent('AppComponent initialized');
    this._router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this._telemetryService.logPageView('PageView', event.urlAfterRedirects);
      }
    });

    // Get feature flags value for the technical issues banner
    this.showTechnicalIssuesBanner = this._featureFlags.enableTechnicalIssuesBanner;

    //Sets title of the app dynamically based on the application brand
    this.titleService.setTitleBasedOnBrand();

    // subscribe to the service worker update observable
    this.swUpdate.versionUpdates.subscribe((event: VersionEvent) => {
      switch (event.type) {
        case 'VERSION_DETECTED':
          this._telemetryService.logEvent('New app version detected. Reloading s');
          this.reloadSite();
          break;
        case 'VERSION_READY':
          break;
        case 'NO_NEW_VERSION_DETECTED':
          break;
        case 'VERSION_INSTALLATION_FAILED':
          this._telemetryService.logException(new Error(`Failed to install app version ${event.version.hash}`));
          alert('Failed to update the app. Please reload the page to get the latest version.');
          break;
        default:
          this._telemetryService.logException(new Error(`Unknown SW Update event type. Unable to determine if new version is available.`));
          alert('Failed to update the app. Please reload the page to get the latest version.');
      }
    });

    //Initialize Mixpanel SDK before calling any other mixpanel events
    this.mixPanelService.init();
    this.authenticationService.forceAuthenticationIfUsernameInactive();
    this.authenticationService.listenForAndHandleInProgressLogin();

    //Subscribe to the http service to check if the user is authenticated.
    //This is used to display the warning banner if the user is not authenticated.
    this._policyService.isAuthorized$.subscribe(isAuthorized => {
      this.isUserAuthorized = isAuthorized;
      if (!isAuthorized) {
        this.warningMessage = this._brandingService.unauthorizedWarningMessage;
      }
    });

    this.authenticationService.isAuthenticated$.subscribe(isAuthenticated => {
      if(isAuthenticated){
        this._idleService.reset();
        this.isAuthenticated = isAuthenticated;
      }
    });

    // Subscribe to the filters service to get the confirmed filters
    this.confirmedFiltersSubscription = this.filtersService.filtersConfirmed.subscribe(f => {
      this.confirmedFilters = f;
      this.trackMixPanelEvent();
    });

    if (this._startupQueryParams.destination) {
      this.setFiltersForOneCustomer(this._startupQueryParams.destination);
    }

    this.mixPanelService.track("EBR App Loaded");
  }

  reloadSite(): void {
    location.reload();
  }

  ngOnDestroy(): void {
    this.filtersSubscription?.unsubscribe();
    this.alertsSubscription?.unsubscribe();
    this.authenticationSubscription?.unsubscribe();
  }

  private trackMixPanelEvent() {
    if (this.confirmedFilters?.regions != null) {
        this.mixPanelService.track('Home page loaded', {
            "regionSelected": this.confirmedFilters?.regions,
            "countriesSelected": this.confirmedFilters?.countries,
            "H5s": this.confirmedFilters?.topLevelHierarchies?.map(topLevelHierarchy => topLevelHierarchy.h5Number),
            "H7s": this.confirmedFilters?.h7Agreements?.map(h7 => h7.number),
            "customerNumbersSelected": this.confirmedFilters?.customers?.map(customer => customer.accountNumber),
            "periodSelected": this.confirmedFilters?.period,
            "Custom Date Range": this.confirmedFilters?.dateRange,
            "templateSelected": this.confirmedFilters?.presentationType,
            "Page Name": this.pageTitle
        });
        this.mixPanelService.initializeActiveDurationTracking(); // start active duration tracking
    }
  }

  private async setFiltersForOneCustomer(destination: string): Promise<void> {

    if (!this._globalCacheData || !this._globalCacheData.customerName || !this._globalCacheData.customerPrimaryDivision) {
      console.error('No data received from global cache service. Redirecting to filter page. Data: ', this._globalCacheData);
      this._router.navigate(['/filter']);
      return;
    }

    // Set the customer name in the filters service
    this.filtersService.setOneCustomerName(this._globalCacheData.customerName);
    // Set the additional filters from 1C in the filters service
    this.filtersService.oneCustomerFilters.next(this._globalCacheData?.customerFilters);

    //Try settting Custom Date Range
    let period:string = "Custom"
    try{
      //Check if the startDate and endDate are in the correct format YYYY-MM
      let regex = new RegExp(/^\d{4}-\d{2}/); //matches YYYY-MM format at the beginning of the strings
      if(regex.test(this._globalCacheData.startDate) && regex.test(this._globalCacheData.endDate)){
        //Set the custom date range in the filters service
        this.filtersService.setCustomDateRangeStartUsingString(this._globalCacheData?.startDate);
        this.filtersService.setCustomDateRangeEndUsingString(this._globalCacheData?.endDate);
        if(!this.filtersService.isDateRangeWithinLimits()){
          alert('The following date range (' + this._globalCacheData.startDate + ' - ' + this._globalCacheData.endDate + ') is outside of the allowed limits (Past 36 Months, not including current). Defaulting to R12.');
          throw new Error('Custom date range is outside of the allowed limits, defaulting to R12');
        }
      }
      else{
        throw new Error('Invalid Date Format');
      }
    }catch(error){
      console.error('Error setting custom date range with {StartDate} and {EndDate}, defaulting to R12.', this._globalCacheData?.startDate, this._globalCacheData?.endDate, error);
      period="R12";
    }


    // Get and map region and country from customerFilters
    const { regions, countries } = await this.extractRegionAndCountries();

    // Customer name will be set in the API Requests using above observable.
    const filters: Filters = new Filters(
      regions,
      countries,
      period,
      true,
      [],
      [],
      [],
      "",
      "");

    await this.filtersService.confirmSelectionsForOneCustomer(filters, this._globalCacheData.customerPrimaryDivision);

    // Redirect based on activated params dest
    this._router.navigate([destination]);
  }


  /**
 * Extracts and maps region and countries from One Customer filters
 * @returns Object containing mapped region code and country codes array
 * @throws Redirects to filter page if Greater China Region is requested
 */
  private async extractRegionAndCountries(): Promise<{ regions: string[]; countries: string[] }> {
    let regions: string[] = [];
    let countries: string[] = []; // Will be populated based on region

    try {
      if (this._globalCacheData.customerFilters) {
        const oneCustomerRegion = this._globalCacheData.customerFilters?.ecolabGroup?.region;
        const oneCustomerCountry = this._globalCacheData.customerFilters?.ecolabGroup?.country;

        // Extract region from 1C filters and map to EBR regions
        if (oneCustomerRegion) {
          const mappedRegionCode = ONE_CUSTOMER_TO_EBR_REGION_MAP[oneCustomerRegion];

          if (mappedRegionCode) {
            if (mappedRegionCode === 'CHINA_NOT_SUPPORTED') {
              // Handle Greater China Region exception
              alert('Data for Greater China Region is currently not available in EBR. Please select a different region.');
              console.error('Greater China Region is not supported. Region from 1C:', oneCustomerRegion);
              this._router.navigate(['/filter']);
              throw new Error('Greater China Region is not supported');
            }

            if (!regions.includes(mappedRegionCode)) {
              regions.push(mappedRegionCode);
            }
          } else {
            console.warn(`Unknown region received from 1C: '${oneCustomerRegion}'. Defaulting to NA.`);
            this._telemetryService.logEvent('Unknown 1C Region', { region: oneCustomerRegion });
            regions.push('NA'); // Explicitly default to NA for unknown regions
          }
        } else {
          console.warn('No region found in customerFilters. Defaulting to NA.');
          regions.push('NA'); // Explicitly default to NA when no region provided
        }

        // Always fetch all countries for the region
        // Note: Due to country name/code mismatches between 1C and EBR, (We don't use countries for 1C though, its just to pass it to the APIs)
        // we fetch all countries for the region and let the customer_lookup_vw view handle filtering
        const countryObjects = await this._countriesService.getCountries(regions, this._globalCacheData.customerPrimaryDivision);
        countries = countryObjects.map(country => country.code);
      } else {
        // No region or country filters at all - use all countries for default NA region
        const countryObjects = await this._countriesService.getCountries(regions, this._globalCacheData.customerPrimaryDivision);
        countries = countryObjects.map(country => country.code);
      }
    } catch (error) {
      console.error('Error extracting region/country from customerFilters:', error);
      this._telemetryService.logEvent('Region Parsing Error', { error: error });
      // Keep default NA region and get all NA countries
      if (regions.length === 0) {
        regions.push('NA');
      }
      const countryObjects = await this._countriesService.getCountries(regions, this._globalCacheData.customerPrimaryDivision);
      countries = countryObjects.map(country => country.code);
    }

    return { regions, countries };
  }
}

